import fs from 'fs'
import { z } from 'zod'

import { serviceDirectory } from './paths'

// A template is data, not code: `template.json` declares what the template asks the user, what it fixes for
// them, and which files it writes — so adding a template never touches `src/`. Everything a manifest can
// express is validated here; in particular `validate` names a built-in rule rather than shipping a pattern,
// so a template cannot widen what counts as a legal service id.

export const SERVICE_ID_PATTERN = /^[a-z][a-z0-9-]*$/

export interface ValidationContext {
  /** The project being scaffolded into — an id is only "taken" relative to one project. */
  projectRoot: string
}

/** Returns an error message, or null when the value is acceptable. */
export type Validator = (value: string, context: ValidationContext) => string | null

export const VALIDATORS = {
  // Both halves of "is this a usable id" live together, so the prompt can re-ask on either. Availability is
  // checked here rather than at write time so the user finds out while they are still on that question.
  serviceId: (value, context) => {
    if (!SERVICE_ID_PATTERN.test(value)) {
      return 'must start with a lowercase letter and contain only lowercase letters, digits and dashes ' +
        '— it becomes the folder name and the service id, which is frozen once deployed'
    }

    if (fs.existsSync(serviceDirectory(value, context.projectRoot))) {
      return `is already taken — services/${ value } exists in this project`
    }

    return null
  },
  nonEmpty: value => (value.trim().length > 0 ? null : 'must not be empty'),
} satisfies Record<string, Validator>

export type ValidatorName = keyof typeof VALIDATORS

const VALIDATOR_NAMES = Object.keys(VALIDATORS) as [ValidatorName, ...ValidatorName[]]

const StringPromptSchema = z.object({
  name    : z.string().min(1),
  label   : z.string().min(1),
  type    : z.literal('string'),
  hint    : z.string().optional(),
  // May interpolate a value fixed by `values`, or an answer collected *earlier* in the list:
  // "{{id}}", "{{id|titleCase}}".
  default : z.string().default(''),
  validate: z.enum(VALIDATOR_NAMES).optional(),
})

const BooleanPromptSchema = z.object({
  name   : z.string().min(1),
  label  : z.string().min(1),
  type   : z.literal('boolean'),
  hint   : z.string().optional(),
  default: z.boolean().default(false),
})

// `default` must name one of the options — checked in the registry rather than here, because a refined
// schema cannot sit inside a discriminated union.
const ChoicePromptSchema = z.object({
  name   : z.string().min(1),
  label  : z.string().min(1),
  type   : z.literal('choice'),
  hint   : z.string().optional(),
  default: z.string().min(1),
  options: z.array(z.object({
    value      : z.string().min(1),
    label      : z.string().min(1),
    description: z.string().optional(),
  })).min(2),
})

export const PromptSchema = z.discriminatedUnion('type', [
  StringPromptSchema,
  BooleanPromptSchema,
  ChoicePromptSchema,
])

export const TemplateFileSchema = z.object({
  from: z.string().min(1),
  to  : z.string().min(1),
  /** Name of a boolean answer; the file is written only when that answer is true. */
  when: z.string().min(1).optional(),
})

export const TemplateManifestSchema = z.object({
  id         : z.string().regex(SERVICE_ID_PATTERN),
  label      : z.string().min(1),
  description: z.string().min(1),
  prompts    : z.array(PromptSchema).min(1),
  /**
   * Answers the template fixes instead of asking for. Available to the file templates exactly like a
   * prompted answer, and still overridable with `--set name=value`.
   */
  values     : z.record(z.string(), z.union([z.string(), z.boolean()])).default({}),
  files      : z.array(TemplateFileSchema).min(1),
})

export type Prompt = z.infer<typeof PromptSchema>
export type TemplateFile = z.infer<typeof TemplateFileSchema>
export type TemplateManifest = z.infer<typeof TemplateManifestSchema>

export type Answers = Record<string, string | boolean>

/** A manifest plus where it was loaded from, so file paths resolve against the right template. */
export interface Template {
  manifest: TemplateManifest
  /** Absolute path of the template directory (the one holding `template.json`). */
  directory: string
  /** Whether this is the default template. Decided by the registry, not declared by the manifest. */
  isDefault: boolean
}

